import { useMemo, useState } from "react";
import {
  List,
  TextField,
  DatagridConfigurable,
  DateField,
  NumberField,
  useTheme,
  FunctionField,
  RecordContextProvider,
  useTranslate,
  ChipField,
} from "react-admin";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import DownloadAction from "../../components/tableListActions/DownloadActionNew";
import SaleReturnCardExpandList from "../../components/saleReturnCards/expand/SaleReturnCardExpandList";
import saleReturnCardFilterList from "../../components/saleReturnCards/filter/saleReturnCardFilterList";
import SaleReturnCardAsideList from "../../components/saleReturnCards/aside/SaleReturnCardAsideList";
import ManualReportList from "../../components/reports/ManualReportList";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const SaleReturnCardList = ({
  withActions = true,
  withFilters = true,
  grandTotalResource = "sale-return-cards",
  title = "mine.list_title.sale_return",
  disableSyncWithLocation = false,
  parentFilter,
  parentStartDate,
  parentEndDate,
}) => {
  const translate = useTranslate();
  const [theme] = useTheme("light");
  const [appliedStandaloneFilter, setAppliedStandaloneFilter] = useState(null);
  const defaultFilterValues = useMemo(
    () => ({ start_date: firstDate, end_date: lastDate, approved: 1 }),
    []
  );
  const { permissions, currentCompanyFilter, filterDefaultValues } =
    useCompanyScopedListFilters(defaultFilterValues);

  const appliedFilter = parentFilter || appliedStandaloneFilter;

  const sharedFilter = useMemo(
    () => ({
      ...filterDefaultValues,
      ...(appliedFilter || {}),
      ...(currentCompanyFilter.company_id
        ? { company_id: currentCompanyFilter.company_id }
        : {}),
      start_date:
        parentStartDate || appliedFilter?.start_date || firstDate,
      end_date: parentEndDate || appliedFilter?.end_date || lastDate,
    }),
    [
      appliedFilter,
      currentCompanyFilter.company_id,
      filterDefaultValues,
      parentStartDate,
      parentEndDate,
    ]
  );

  const primaryFilter = useMemo(
    () =>
      withFilters
        ? { ...currentCompanyFilter, approved: 1, type: 2 }
        : { ...sharedFilter, approved: 1, type: 2 },
    [currentCompanyFilter, sharedFilter, withFilters]
  );

  const brokenFilter = useMemo(
    () => ({ ...sharedFilter, approved: 1, type: 2 }),
    [sharedFilter]
  );

  const goodFilter = useMemo(
    () => ({ ...sharedFilter, approved: 1, type: 1 }),
    [sharedFilter]
  );

  const grandTotalFilter = useMemo(
    () => ({ ...sharedFilter, type: undefined }),
    [sharedFilter]
  );

  const primaryFilterDefaultValues = withFilters
    ? filterDefaultValues
    : sharedFilter;

  const list = useMemo(() => {
    const PrimaryList = withFilters ? ManualReportList : List;
    return (
      <>
        <PrimaryList
          storeKey={false}
          disableSyncWithLocation={disableSyncWithLocation}
          resource="sale-return-cards"
          title={title}
          empty={false}
          aside={
            <SaleReturnCardAsideList
              name="RETUR JUAL PECAH"
              filter={brokenFilter}
              withGrandTotal={false}
            />
          }
          actions={withActions ? <DownloadAction /> : false}
          filters={
            withFilters ? saleReturnCardFilterList(permissions) : undefined
          }
          filter={primaryFilter}
          filterDefaultValues={primaryFilterDefaultValues}
          requiredFilters={["start_date", "end_date"]}
          onApplied={setAppliedStandaloneFilter}
          onReset={() => setAppliedStandaloneFilter(null)}
          queryOptions={{ meta: { sort: ["created_at DESC"] } }} // overide default sort
          sx={{
            flexDirection: "column",
          }}
        >
          <DatagridConfigurable
            bulkActionButtons={false}
            expand={<SaleReturnCardExpandList />}
            sx={{
              "& .RaDatagrid-expandedPanel > td": {
                p: 0,
                bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
              },
            }}
          >
            {permissions && permissions.update_company ? (
              <TextField label="mine.label.depo" source="company.alias" />
            ) : null}
            <TextField source="code" label="mine.label.receipt_no" />
            <DateField
              source="date"
              label="mine.label.date"
              options={{
                year: "numeric",
                month: "short",
                day: "2-digit",
              }}
            />
            <FunctionField
              label="mine.label.customer"
              render={(record) =>
                `${record.customer.name} (${record.customer.code})`
              }
            />
            <FunctionField
              label="mine.label.type"
              textAlign="center"
              render={(record) => {
                let rec = {};
                switch (record.type) {
                  case 1:
                    rec.type = translate("mine.radio_btn.return_warehouse");
                    rec.color = "success";
                    break;
                  case 2:
                    rec.type = translate("mine.radio_btn.return_broken");
                    rec.color = "error";
                    break;
                  default:
                    rec.type = "-";
                    rec.color = "default";
                }
                return (
                  <RecordContextProvider value={rec}>
                    <ChipField source="type" color={rec.color} size="small" />
                  </RecordContextProvider>
                );
              }}
            />
            <FunctionField
              textAlign="right"
              label="Box"
              render={(record) => {
                let rec = { total_return: 0 };
                for (let i = 0; i < record.sale_return_items.length; i++) {
                  rec.total_return +=
                    +record.sale_return_items[i].return_quantity;
                }
                return (
                  <RecordContextProvider value={rec}>
                    <NumberField source="total_return" />
                  </RecordContextProvider>
                );
              }}
            />
            <NumberField source="total" label="mine.label.value" />
            <TextField source="note" label="mine.label.note" emptyText="-" />
          </DatagridConfigurable>
        </PrimaryList>
        <div style={{ margin: "22px 0 8px 0" }} /> {/* ================ */}
        {(!withFilters || appliedStandaloneFilter) ? <List
          key={`good-${JSON.stringify(goodFilter)}`}
          storeKey={false}
          disableSyncWithLocation={true}
          resource="sale-return-cards"
          title=" "
          empty={false}
          aside={
            <SaleReturnCardAsideList
              name="RETUR JUAL BAIK"
              filter={goodFilter}
              withGrandTotal={true}
              grandTotalResource={grandTotalResource}
              grandTotalFilter={grandTotalFilter}
            />
          }
          actions={false}
          filter={goodFilter}
          filterDefaultValues={sharedFilter}
          queryOptions={{
            meta: { sort: ["created_at DESC"] },
          }} // overide default sort
          sx={{
            flexDirection: "column",
          }}
        >
          <DatagridConfigurable
            bulkActionButtons={false}
            expand={<SaleReturnCardExpandList />}
            sx={{
              "& .RaDatagrid-expandedPanel > td": {
                p: 0,
                bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
              },
            }}
          >
            {permissions && permissions.update_company ? (
              <TextField label="mine.label.depo" source="company.alias" />
            ) : null}
            <TextField source="code" label="mine.label.receipt_no" />
            <DateField
              source="date"
              label="mine.label.date"
              options={{
                year: "numeric",
                month: "short",
                day: "2-digit",
              }}
            />
            <FunctionField
              label="mine.label.customer"
              render={(record) =>
                `${record.customer.name} (${record.customer.code})`
              }
            />
            <FunctionField
              label="mine.label.type"
              textAlign="center"
              render={(record) => {
                let rec = {};
                switch (record.type) {
                  case 1:
                    rec.type = translate("mine.radio_btn.return_warehouse");
                    rec.color = "success";
                    break;
                  case 2:
                    rec.type = translate("mine.radio_btn.return_broken");
                    rec.color = "error";
                    break;
                  default:
                    rec.type = "-";
                    rec.color = "default";
                }
                return (
                  <RecordContextProvider value={rec}>
                    <ChipField source="type" color={rec.color} size="small" />
                  </RecordContextProvider>
                );
              }}
            />
            <FunctionField
              textAlign="right"
              label="Box"
              render={(record) => {
                let rec = { total_return: 0 };
                for (let i = 0; i < record.sale_return_items.length; i++) {
                  rec.total_return +=
                    +record.sale_return_items[i].return_quantity;
                }
                return (
                  <RecordContextProvider value={rec}>
                    <NumberField source="total_return" />
                  </RecordContextProvider>
                );
              }}
            />
            <NumberField source="total" label="mine.label.value" />
            <TextField source="note" label="mine.label.note" emptyText="-" />
          </DatagridConfigurable>
        </List> : null}
      </>
    );
  }, [
    theme,
    permissions,
    disableSyncWithLocation,
    brokenFilter,
    primaryFilter,
    primaryFilterDefaultValues,
    goodFilter,
    grandTotalFilter,
    sharedFilter,
    currentCompanyFilter,
    withFilters,
    appliedStandaloneFilter,
  ]);
  return list;
};

export default SaleReturnCardList;
